/**
 * Utility functions for managing project-specific group-by settings persistence
 */

const GROUP_BY_STORAGE_KEY = 'taskGroupBySettings';

/**
 * Get the current project ID from localStorage
 */
const getCurrentProjectId = () => {
    return localStorage.getItem('project_id');
};

/**
 * Get project-specific group-by settings from localStorage
 * @param {string} projectId - The project ID
 * @returns {Object} Group-by settings for the project
 */
export const getGroupBySettings = (projectId = null) => {
    try {
        const currentProjectId = projectId || getCurrentProjectId();
        if (!currentProjectId) return null;

        const stored = localStorage.getItem(GROUP_BY_STORAGE_KEY);
        if (!stored) return null;

        const allSettings = JSON.parse(stored);
        return allSettings[currentProjectId] || null;
    } catch (error) {
        console.error('Error loading group-by settings:', error);
        return null;
    }
};

/**
 * Save project-specific group-by settings to localStorage
 * @param {string} projectId - The project ID
 * @param {Object} settings - Group-by settings to save
 */
export const saveGroupBySettings = (projectId = null, settings) => {
    try {
        const currentProjectId = projectId || getCurrentProjectId();
        if (!currentProjectId) return;

        const stored = localStorage.getItem(GROUP_BY_STORAGE_KEY);
        const allSettings = stored ? JSON.parse(stored) : {};

        allSettings[currentProjectId] = {
            column: settings.column || '',
            sort: settings.sort || '',
            collapsedGroups: settings.collapsedGroups || [],
        };

        localStorage.setItem(GROUP_BY_STORAGE_KEY, JSON.stringify(allSettings));
    } catch (error) {
        console.error('Error saving group-by settings:', error);
    }
};

/**
 * Clear group-by settings for a specific project
 * @param {string} projectId - The project ID
 */
export const clearGroupBySettings = (projectId = null) => {
    try {
        const currentProjectId = projectId || getCurrentProjectId();
        if (!currentProjectId) return;

        const stored = localStorage.getItem(GROUP_BY_STORAGE_KEY);
        if (!stored) return;

        const allSettings = JSON.parse(stored);
        delete allSettings[currentProjectId];
        localStorage.setItem(GROUP_BY_STORAGE_KEY, JSON.stringify(allSettings));
    } catch (error) {
        console.error('Error clearing group-by settings:', error);
    }
};

/**
 * Clear all group-by settings for all projects
 */
export const clearAllGroupBySettings = () => {
    try {
        localStorage.removeItem(GROUP_BY_STORAGE_KEY);
    } catch (error) {
        console.error('Error clearing all group-by settings:', error);
    }
};
